import { Controller } from 'stimulus';
const TomSelect = require('../lib/tom_select');

// Floating popup editor for the new "cards" tasks index. One shared popup
// element appended to <body> and reused across every editable region (status,
// people, equipment, start/end dates, reminder). Sibling of
// jobs_popup_editor_controller — same lifecycle, single generic `openField`
// opener keyed by data-task-id + data-field.
//
// Because the popup lives outside this.element (so it can float with
// position: fixed without clipping), Stimulus 1.x doesn't auto-wire its
// data-action / data-target attributes; we bind them manually after each
// innerHTML injection.
//
// The Schedule's chips and its task pop-up open the same windows: the trigger
// (or the pop-up around it, data-popup-save-url) carries the save URL
// (SchedulesController#plan_field), which answers in JSON like every
// Schedule edit, and tasks:window-saved hands that answer to schedule-plan
// for its note and Undo. Opened from inside the pop-up (data-popup-anchor),
// the window takes the pop-up's place. tasks:window-open and
// tasks:window-closed (with why: cancel, saved, outside or replace) let
// schedule-plan hide and bring back its pop-up and pin the task in its strip.
export default class extends Controller {
  connect() {
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.boundKeydown = this.handleGlobalKey.bind(this);
    document.addEventListener('click', this.boundOutsideClick, true);
    document.addEventListener('keydown', this.boundKeydown);
    this.popupElement = document.createElement('div');
    this.popupElement.className = 'hidden fixed bg-white border rounded-lg shadow-lg';
    this.popupElement.style.zIndex = '9999';
    document.body.appendChild(this.popupElement);
    this.currentTrigger = null;
    this.activeEditor = null;
    // A popup that draws its own contents once open (the Duration and Dates
    // window's calendar) asks to be placed again at its real height
    this.boundResized = () => {
      if (this.currentTrigger && !this.popupElement.classList.contains('hidden')) {
        this.position(this.currentTrigger);
        this.fit();
      }
    };
    document.addEventListener('popup:resized', this.boundResized);
    // It stays on screen as it grows (fit), and when the window is resized
    this.boundFit = () => this.fit();
    window.addEventListener('resize', this.boundFit);
    if (window.ResizeObserver) this.resizeObserver = new ResizeObserver(this.boundFit);
  }

  disconnect() {
    document.removeEventListener('click', this.boundOutsideClick, true);
    document.removeEventListener('keydown', this.boundKeydown);
    document.removeEventListener('popup:resized', this.boundResized);
    window.removeEventListener('resize', this.boundFit);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.popupElement && this.popupElement.parentNode) {
      this.popupElement.parentNode.removeChild(this.popupElement);
    }
  }

  // Trigger carries data-task-id and data-field. The matching display element
  // (id="task-<id>-field-<field>") is swapped with the save response.
  openField(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    const taskId = trigger.dataset.taskId;
    const field = trigger.dataset.field;
    if (!taskId || !field) return;

    const query = new URLSearchParams({ field });
    // Density of the card the click came from — so the save response comes back
    // styled for that card (a compact row that's been individually expanded
    // still gets the right pill). Defaults to expanded server-side.
    if (trigger.dataset.variant) query.set('variant', trigger.dataset.variant);
    // The Schedule's: the chip's own save URL, or the task pop-up's
    const scope = trigger.closest('[data-popup-save-url]');
    const planUrl = trigger.dataset.saveUrl || (scope ? scope.dataset.popupSaveUrl.replace('FIELD', encodeURIComponent(field)) : null);
    // Inside the Schedule's pop-up: the window takes its place (where it is now)
    const frame = trigger.closest('[data-popup-anchor]');

    this.beginEdit(trigger, {
      taskId,
      formUrl: `/tasks/${taskId}/popup_field_form?${query}`,
      saveUrl: planUrl || `/tasks/${taskId}/popup_field_save?${query}`,
      plan: !!planUrl,
      at: frame ? frame.getBoundingClientRect() : null,
      saveMethod: 'PATCH',
      refreshTargetId: `task-${taskId}-field-${field}`,
      prepareFormData: (popup) => {
        const fd = new FormData();
        popup.querySelectorAll('input[name], select[name], textarea[name]').forEach((el) => {
          if (el.tagName === 'SELECT' && el.multiple) {
            Array.from(el.selectedOptions).forEach((opt) => fd.append(el.name, opt.value));
          } else if (el.type === 'checkbox' || el.type === 'radio') {
            if (el.checked) fd.append(el.name, el.value);
          } else {
            fd.append(el.name, el.value);
          }
        });
        return fd;
      }
    });
  }

  // ─── Shared lifecycle ──────────────────────────────────────────────────────

  beginEdit(trigger, editorDescriptor) {
    this.close('replace');
    this.currentTrigger = trigger;
    this.activeEditor = editorDescriptor;

    fetch(editorDescriptor.formUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(async (r) => {
        // Only a popup fragment may be injected. A redirect (sign-in page, or
        // a permission bounce from a server that still redirects) means the
        // body is a whole page, and an error status means it is a message —
        // show a notice rather than the page inside the popup.
        if (r.redirected || !r.ok) {
          this.showNotice(trigger, await this.blockedMessage(r));
          return;
        }
        const html = await r.text();
        this.popupElement.innerHTML = html;
        this.bindPopupEvents();
        this.popupElement.style.visibility = 'hidden';
        this.popupElement.classList.remove('hidden');
        this.position(trigger);
        this.fitMoved = false;
        this.fit();
        this.watchSize();
        this.popupElement.style.visibility = '';
        const first = this.popupElement.querySelector('input, select, textarea');
        if (first) first.focus();
        document.dispatchEvent(new CustomEvent('tasks:window-open', { detail: { taskId: editorDescriptor.taskId, inPlace: !!editorDescriptor.at } }));
      });
  }

  // why: cancel (Cancel or Escape), saved, outside (a click elsewhere) or
  // replace (another window opening)
  close(why = 'cancel') {
    if (!this.popupElement) return;
    if (this.tomSelectInstances) {
      this.tomSelectInstances.forEach((ts) => { try { ts.destroy(); } catch (_) { /* noop */ } });
      this.tomSelectInstances = [];
    }
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.popupElement.classList.add('hidden');
    this.popupElement.innerHTML = '';
    this.popupElement.style.maxHeight = '';
    this.popupElement.style.overflowY = '';
    const t = this.currentTrigger;
    this.currentTrigger = null;
    this.activeEditor = null;
    if (t) {
      document.dispatchEvent(new CustomEvent('tasks:window-closed', { detail: { why } }));
      if (document.body.contains(t)) t.focus();
    }
  }

  bindPopupEvents() {
    const cancelBtn = this.popupElement.querySelector('[data-popup-action="cancel"]');
    if (cancelBtn) cancelBtn.addEventListener('click', (e) => { e.preventDefault(); this.close(); });

    const saveBtn = this.popupElement.querySelector('[data-popup-action="save"]');
    if (saveBtn) saveBtn.addEventListener('click', (e) => { e.preventDefault(); this.save(); });

    this.popupElement.querySelectorAll('input, select, textarea').forEach((input) => {
      input.addEventListener('keydown', (e) => this.handleKey(e));
    });

    this.tomSelectInstances = [];
    this.popupElement.querySelectorAll('[data-popup-multi-select]').forEach((el) => {
      const ts = new TomSelect(el, {
        plugins: ['remove_button'],
        persist: false,
        maxItems: null,
        hideSelected: true,
        create: el.dataset.popupAllowCreate === 'true',
        onItemAdd: function () {
          this.setTextboxValue('');
          this.refreshOptions(false);
        }
      });
      this.tomSelectInstances.push(ts);
    });
  }

  save() {
    if (!this.activeEditor) return;
    const { saveUrl, saveMethod, refreshTargetId, prepareFormData, taskId } = this.activeEditor;
    const body = prepareFormData(this.popupElement);

    this.clearErrors();
    this.showSpinner(true);

    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const csrf = csrfMeta ? csrfMeta.content : '';
    if (this.activeEditor.plan) {
      this.savePlan(saveUrl, saveMethod, body, csrf);
      return;
    }

    fetch(saveUrl, {
      method: saveMethod,
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': csrf, Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body
    })
      .then(async (response) => {
        if (response.ok && !response.redirected) {
          const html = await response.text();
          const target = document.getElementById(refreshTargetId);
          if (target) {
            target.innerHTML = html;
            this.syncStatusVisuals(target);
          }
          const warning = response.headers.get('X-Toast-Warning');
          if (warning && window.toastr) window.toastr.warning(decodeURIComponent(warning), null, { escapeHtml: true });
          this.close('saved');
          // (the Tasks tab's one view draws the row again, and its readiness strip follows)
          if (taskId) document.dispatchEvent(new CustomEvent('tasks:changed', { detail: { taskId, field: refreshTargetId.split('-field-')[1] } }));
          return;
        }
        let messages = [];
        const contentType = response.headers.get('content-type') || '';
        if (response.redirected) {
          messages = ['This change could not be saved. Reload the page and try again.'];
        } else if (contentType.includes('text/plain')) {
          const text = (await response.text()).trim();
          messages = [text || `Save failed (${response.status})`];
        } else if (contentType.includes('application/json')) {
          try {
            const json = await response.json();
            messages = Array.isArray(json.errors) ? json.errors : [String(json.error || response.statusText)];
          } catch (_) {
            messages = [`Save failed (${response.status})`];
          }
        } else {
          messages = [`Save failed (${response.status})`];
        }
        this.showErrors(messages);
        this.showSpinner(false);
      })
      .catch((err) => {
        console.error('Popup save failed:', err);
        this.showErrors(['Could not reach the server. Check your connection and try again.']);
        this.showSpinner(false);
      });
  }

  // A window opened from the Schedule: its answer (the note's words, Undo,
  // what else the plan moved) goes to schedule-plan, which draws the grid again
  savePlan(url, method, body, csrf) {
    fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': csrf, Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body
    })
      .then((response) => response.json().catch(() => ({ ok: false })))
      .then((json) => {
        if (json.ok) {
          document.dispatchEvent(new CustomEvent('tasks:window-saved', { detail: json }));
          this.close('saved');
          return;
        }
        this.showErrors([json.error || 'That could not be saved.']);
        this.showSpinner(false);
      })
      .catch(() => {
        this.showErrors(['Could not reach the server. Check your connection and try again.']);
        this.showSpinner(false);
      });
  }

  // A status change recolors the card's left stripe too. The swapped-in status
  // pill carries the new color as data-stripe-color; copy it onto the stripe in
  // the same card/row (.jlv-stripe expanded · .tlv-rstripe compact). No-op for
  // other fields, whose fragments don't carry the attribute.
  // Status and start/end date edits shift status-derived bits beyond the swapped
  // fragment: the left stripe color (status only) and the timing line
  // ("Due in 3 days" → "Completed"/"late"). The fragment carries them as data-*
  // attrs; apply to the same card/row so it reflects the change without a reload.
  // No-op for other fields, whose fragments don't carry these attributes.
  syncStatusVisuals(target) {
    const source = target.querySelector('[data-timing-phrase]');
    if (!source) return;
    const card = target.closest('[name="task"]');
    if (!card) return;
    const d = source.dataset;

    // Left stripe (.jlv-stripe expanded · .tlv-rstripe compact) — status edits only.
    if (d.stripeColor) {
      const stripe = card.querySelector('.jlv-stripe, .tlv-rstripe');
      if (stripe) stripe.style.background = d.stripeColor;
    }

    // Timing line — expanded .jlv-timing (<b>phrase</b>), compact .tlv-rtiming.
    const overdue = d.timingOverdue === 'true';
    const expanded = card.querySelector('.jlv-timing');
    if (expanded) {
      expanded.classList.toggle('jlv-timing--over', overdue);
      expanded.textContent = '';
      const b = document.createElement('b');
      b.textContent = d.timingPhrase;
      expanded.appendChild(b);
    }
    const compact = card.querySelector('.tlv-rtiming');
    if (compact) {
      compact.classList.toggle('tlv-rtiming--over', overdue);
      compact.textContent = d.timingPhrase;
    }
  }

  // Message for a form fetch that did not return a popup fragment: the
  // server's plain-text reason for a refused request, otherwise a generic
  // nudge (a redirect means the page or session moved on).
  async blockedMessage(response) {
    const contentType = response.headers.get('content-type') || '';
    if (!response.redirected && !response.ok && contentType.includes('text/plain')) {
      const text = (await response.text()).trim();
      if (text) return text;
    }
    return 'This editor could not be opened. Reload the page and try again.';
  }

  // Text-only popup in place of the editor. Escape / outside click close it.
  showNotice(trigger, message) {
    this.popupElement.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'p-3 text-xs text-gray-700';
    box.style.width = '260px';
    box.dataset.popupRole = 'notice';
    box.textContent = message;
    this.popupElement.appendChild(box);
    this.popupElement.classList.remove('hidden');
    this.position(trigger);
  }

  clearErrors() {
    const existing = this.popupElement.querySelector('[data-popup-role="errors"]');
    if (existing) existing.remove();
  }

  showErrors(messages) {
    this.clearErrors();
    const errEl = document.createElement('div');
    errEl.dataset.popupRole = 'errors';
    errEl.className = 'mt-2 p-2 text-xs rounded';
    errEl.style.cssText = 'background:#fee2e2;color:#991b1b;border:1px solid #fecaca;';
    errEl.innerHTML = messages.map((m) => `<div>${this.escapeHtml(m)}</div>`).join('');
    const buttons = this.popupElement.querySelector('[data-popup-action="save"]');
    const buttonsRow = buttons ? buttons.parentElement : null;
    if (buttonsRow) {
      buttonsRow.parentNode.insertBefore(errEl, buttonsRow);
    } else {
      this.popupElement.appendChild(errEl);
    }
  }

  escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  handleKey(event) {
    // A box with keys of its own (the equipment window's search) handles them
    if (event.target.hasAttribute('data-popup-no-enter')) return;
    if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      this.save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  handleGlobalKey(event) {
    if (event.key === 'Escape' && this.popupElement && !this.popupElement.classList.contains('hidden')) {
      this.close();
    }
  }

  handleOutsideClick(event) {
    if (!this.popupElement || this.popupElement.classList.contains('hidden')) return;
    // A click on something the popup just redrew (a search result) is inside it
    if (!event.target.isConnected) return;
    if (this.popupElement.contains(event.target)) return;
    if (this.currentTrigger && this.currentTrigger.contains(event.target)) return;
    this.close('outside');
  }

  // Keeps the popup on screen as its contents grow (the equipment window's
  // pickers draw after it opens; a search adds rows): its top stays where it
  // opened and it is only as tall as the room down to the bottom of the
  // screen. A window with its own header (.te-popup: the equipment and
  // Person windows) keeps Save in view and scrolls its body; any other popup
  // scrolls as a whole. Opened too low to have room, it moves up once, so it
  // doesn't jump while someone types.
  fit() {
    const popup = this.popupElement;
    if (!popup || popup.classList.contains('hidden') || !this.currentTrigger) return;
    const margin = 8;
    const frame = popup.querySelector('.te-popup') || popup;
    const chrome = frame === popup ? 0 : popup.offsetHeight - frame.offsetHeight;
    const wanted = this.contentHeight(frame) + chrome;
    let { top } = popup.getBoundingClientRect();
    if (!this.fitMoved && window.innerHeight - top - margin < Math.min(wanted, 360)) {
      top = Math.max(margin, window.innerHeight - Math.min(wanted, window.innerHeight - 2 * margin) - margin);
      popup.style.top = `${top + window.scrollY}px`;
      this.fitMoved = true;
    }
    frame.style.maxHeight = `${Math.max(window.innerHeight - top - margin - chrome, 160)}px`;
    if (frame === popup) popup.style.overflowY = 'auto';
  }

  // The popup's full height, scrolled or not
  contentHeight(frame) {
    const body = frame.querySelector('.te-popup-body');
    if (body) return frame.offsetHeight - body.clientHeight + body.scrollHeight;
    return frame.scrollHeight;
  }

  // Fit again whenever what's inside changes size
  watchSize() {
    if (!this.resizeObserver) return;
    this.resizeObserver.disconnect();
    const inner = this.popupElement.querySelector('.te-popup-body > *') || this.popupElement.firstElementChild;
    if (inner) this.resizeObserver.observe(inner);
  }

  position(trigger) {
    // In the Schedule pop-up's place: its top left, kept on screen
    const at = this.activeEditor && this.activeEditor.at;
    if (at) {
      const width = this.popupElement.getBoundingClientRect().width || 420;
      this.popupElement.style.left = `${Math.max(8, Math.min(at.left, window.innerWidth - width - 8))}px`;
      this.popupElement.style.top = `${Math.max(8, at.top)}px`;
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const popupRect = this.popupElement.getBoundingClientRect();
    const popupWidth = popupRect.width || 260;
    const popupHeight = popupRect.height || 200;
    const margin = 8;

    let left = rect.left + window.scrollX;
    if (left + popupWidth + margin > window.innerWidth + window.scrollX) {
      left = Math.max(margin, window.innerWidth + window.scrollX - popupWidth - margin);
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    let top;
    if (spaceBelow >= popupHeight + 4 || spaceBelow >= spaceAbove) {
      top = rect.bottom + window.scrollY + 4;
    } else {
      top = rect.top + window.scrollY - popupHeight - 4;
      if (top < window.scrollY + margin) top = window.scrollY + margin;
    }
    // A tall popup (the equipment window) that fits neither below nor above
    // its trigger moves up until its foot is on screen, covering the trigger.
    const lowest = window.scrollY + window.innerHeight - popupHeight - margin;
    if (top > lowest) top = Math.max(window.scrollY + margin, lowest);

    this.popupElement.style.left = `${left}px`;
    this.popupElement.style.top = `${top}px`;
  }

  showSpinner(on) {
    const spinner = this.popupElement.querySelector('[data-popup-role="spinner"]');
    const text = this.popupElement.querySelector('[data-popup-role="save-text"]');
    const btn = this.popupElement.querySelector('[data-popup-action="save"]');
    if (spinner) spinner.classList.toggle('hidden', !on);
    if (text) text.textContent = on ? 'Saving…' : 'Save';
    if (btn) btn.disabled = on;
  }
}
